import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ContentProvider } from "@/lib/content";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Olivenoel from "@/pages/Olivenoel";
import Contact from "@/pages/Contact";
import Career from "@/pages/Career";
import Catering from "@/pages/Catering";
import Gutschein from "@/pages/Gutschein";
import Impressum from "@/pages/Impressum";
import Datenschutz from "@/pages/Datenschutz";
import { CookieConsent } from "@/components/CookieConsent";
import { Preloader } from "@/components/Preloader";
import AdminLogin from "@/pages/admin/AdminLogin";
import AuthCallback from "@/pages/admin/AuthCallback";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import LiveSender from "@/pages/admin/LiveSender";
import "@/App.css";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Leitet alte .htm/.html-Adressen der früheren Website auf die neuen sauberen URLs um
const KNOWN_ROUTES = new Set([
  "/", "/ueber-uns", "/olivenoel", "/kontakt", "/karriere", "/catering", "/gutschein", "/impressum", "/datenschutz",
]);
const LEGACY_ALIASES = {
  "/index": "/", "/home": "/", "/start": "/", "/startseite": "/", "/default": "/", "/main": "/",
  "/about": "/ueber-uns", "/ueberuns": "/ueber-uns", "/chi-siamo": "/ueber-uns", "/wir-ueber-uns": "/ueber-uns",
  "/contact": "/kontakt", "/contatti": "/kontakt", "/anfahrt": "/kontakt", "/reservierung": "/kontakt",
  "/olio": "/olivenoel", "/oel": "/olivenoel", "/oliven-oel": "/olivenoel",
  "/speisekarte": "/", "/menu": "/", "/menue": "/", "/karte": "/", "/tageskarte": "/",
  "/galerie": "/", "/gallery": "/", "/impressionen": "/",
  "/jobs": "/karriere", "/stellenangebote": "/karriere", "/bewerbung": "/karriere",
  "/gutscheine": "/gutschein", "/events": "/catering",
};

function legacyTarget(pathname) {
  if (!/\.html?$/i.test(pathname)) return null;
  let base = pathname.replace(/\.html?$/i, "").toLowerCase();
  if (base.length > 1 && base.endsWith("/")) base = base.slice(0, -1);
  if (base === "") base = "/";
  if (LEGACY_ALIASES[base]) return LEGACY_ALIASES[base];
  if (KNOWN_ROUTES.has(base)) return base;
  return "/"; // unbekannte alte Seite -> Startseite
}

function LegacyRedirect() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const target = legacyTarget(pathname);
    if (target) navigate(target + search, { replace: true });
  }, [pathname, search, navigate]);
  return null;
}

function PublicSite() {
  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/ueber-uns" element={<About />} />
          <Route path="/olivenoel" element={<Olivenoel />} />
          <Route path="/kontakt" element={<Contact />} />
          <Route path="/karriere" element={<Career />} />
          <Route path="/catering" element={<Catering />} />
          <Route path="/gutschein" element={<Gutschein />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
        </Routes>
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Process OAuth callback synchronously before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/live-sender" element={<ProtectedRoute><LiveSender /></ProtectedRoute>} />
      <Route path="/*" element={<PublicSite />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Preloader />
      <BrowserRouter>
        <AuthProvider>
          <ContentProvider>
            <ScrollToTop />
            <LegacyRedirect />
            <AppRouter />
            <Toaster position="top-center" richColors />
          </ContentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
